import { registerSchema } from '@argus-labs/auth-shared/auth'
import { VerifyType } from '@argus-labs/auth-shared/verify'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Controller, useForm } from 'react-hook-form'
import type { z } from 'zod'
import { Button } from '@/components/ui/button'
import { ClearableInput } from '@/components/ui/clearable-input'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { PageCard } from '@/components/ui/page-card'
import { otpVerification } from '@/lib/otp-verification'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/_auth/edit/email')({
  component: EditEmail,
})

function EditEmail() {
  return (
    <PageCard title="Edit Email">
      <EditEmailForm />
    </PageCard>
  )
}

// Pick email from register params
const editEmailFormSchema = registerSchema.pick({ email: true })
type EditEmailFormParams = z.infer<typeof editEmailFormSchema>

function EditEmailForm() {
  // Validate on submit, not blur: an error appearing on blur shifts the layout under a
  // click in progress, so a tap on a link or button below the field can miss.
  const form = useForm<EditEmailFormParams>({
    resolver: zodResolver(editEmailFormSchema),
    defaultValues: { email: '' },
  })
  const navigate = useNavigate()

  const onSubmit = async ({ email }: EditEmailFormParams) => {
    try {
      const { data, error } = await otpVerification.requestEmailChange(email)

      if (data) {
        return navigate({
          to: '/verify',
          search: { email, type: VerifyType.ChangeEmail },
        })
      }

      toast.fromError(error)
    } catch {
      toast.fromPreset('NETWORK_ERROR')
    }
  }

  return (
    <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
      <FieldGroup>
        <Controller
          control={form.control}
          name="email"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid} data-disabled={field.disabled}>
              <FieldLabel htmlFor="edit-email-email">Email Address*</FieldLabel>
              <ClearableInput
                {...field}
                id="edit-email-email"
                type="email"
                onClear={() => field.onChange('')}
                required
                autoFocus
                autoComplete="email"
                placeholder="eg: mojo.doe@argus.gg"
                aria-invalid={fieldState.invalid}
                aria-describedby={fieldState.invalid ? 'edit-email-email-error' : undefined}
              />
              <FieldError id="edit-email-email-error" errors={[fieldState.error]} />
            </Field>
          )}
        />

        <Button className="w-full" type="submit" disabled={form.formState.isSubmitting}>
          Update
        </Button>
      </FieldGroup>
    </form>
  )
}
